/**
 * The multi-factor endpoints, split out of `client.ts`.
 *
 * A separate module because they are a separate concern with a separate
 * lifetime: two of them are reached BEFORE any session exists (they are the
 * second half of login and their authorisation is the challenge token), and
 * four are reached from the profile with a session in hand. Keeping them beside
 * `login`/`me`/`logout` would put those two groups in one object and invite a
 * screen to call the wrong one.
 *
 * Everything here goes through `send`, so it inherits the CSRF handshake, the
 * one-shot 419 retry, `credentials: 'include'` and the single `ApiError`
 * envelope. There is no second fetch path in this application.
 *
 * ## Two things this module deliberately does not do
 *
 * - **It does not tell failures apart.** The API answers ONE 422, with one
 *   message, for an unknown token, a wrong code, an expired challenge, a burnt
 *   challenge and a disabled account. The OpenAPI document says so in capitals.
 *   A client that tried to branch would be inventing distinctions the response
 *   does not carry.
 * - **It does not retry.** A wrong code spends one of five challenge attempts
 *   AND one of the account's login attempts. A client that retried would be
 *   burning the user's budget on their behalf.
 */

import { send, type ApiUser } from './client';

/**
 * What `POST /profile/mfa` discloses, and the one place a TOTP secret is ever
 * readable. Show it once; never log it, never persist it, never send it on.
 */
export interface MfaEnrolment {
  secret: string;
  otpauth_uri: string;
}

/** The ten single-use codes, readable at issue and never again. */
export interface RecoveryCodes {
  recovery_codes: readonly string[];
}

export const mfaApi = {
  /* ------------------------------------------- the second half of login */

  /**
   * Exchange a challenge token and a six-digit code for a session.
   *
   * Unauthenticated by design — this is part of what PRODUCES the credential,
   * so requiring one would be circular.
   *
   * NO `remember` PARAMETER (Task 4, M-18, round 1 review fix for m-3). An
   * earlier version accepted one here, resubmitted from the login form's
   * checkbox — independently of whatever the login step itself sent, which
   * meant a caller holding a live challenge and a valid code could send a
   * DIFFERENT value than what was ticked at login. The API now derives it
   * from the challenge row itself (stamped by `POST /auth/login`), so there
   * is nothing for this call to carry.
   */
  verify: (challengeToken: string, code: string) =>
    send<ApiUser>('/auth/mfa/verify', {
      method: 'POST',
      body: { challenge_token: challengeToken, code },
    }),

  /**
   * The lost-phone path. The code is sent AS THE USER TYPED IT: the API folds
   * away separators and case itself, so normalising here would be a second copy
   * of a rule that already exists, free to drift from it.
   *
   * Also derives "remember me" from the challenge row, giving this path full
   * parity with `verify()` above (Task 4, M-18, round 1 review fix for m-2) —
   * nothing for this call to send either.
   */
  redeemRecoveryCode: (challengeToken: string, recoveryCode: string) =>
    send<ApiUser>('/auth/mfa/recovery', {
      method: 'POST',
      body: { challenge_token: challengeToken, recovery_code: recoveryCode },
    }),

  /* --------------------------------------------------- self-service, signed in */

  /** Mint an UNCONFIRMED secret. Answers 409 when a factor is already enabled. */
  begin: () => send<MfaEnrolment>('/profile/mfa', { method: 'POST' }),

  /** Prove the authenticator works. Returns the ten recovery codes, once. */
  confirm: (code: string) =>
    send<RecoveryCodes>('/profile/mfa/confirm', { method: 'POST', body: { code } }),

  /** Turn the factor off. Costs BOTH the password and a current code. */
  disable: (password: string, code: string) =>
    send<null>('/profile/mfa', { method: 'DELETE', body: { password, code } }),

  /** A fresh set of ten. Costs the password only — the lost-phone escape. */
  regenerateRecoveryCodes: (password: string) =>
    send<RecoveryCodes>('/profile/mfa/recovery-codes', { method: 'POST', body: { password } }),
};
